import React, {
  FC,
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import styles from './DevChart.module.scss'
import { DevChartProps } from './DevChart.types'

const PAD = { top: 12, right: 12, bottom: 20, left: 44 }
const Y_TICKS = 4

type Pt = { x: number; y: number }

function buildPath(pts: Pt[]): string {
  if (pts.length < 2) return ''
  if (pts.length > 120) {
    return 'M' + pts.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join('L')
  }
  let d = `M${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`
  for (let i = 1; i < pts.length; i++) {
    const p0 = pts[i - 1]
    const p1 = pts[i]
    const cp = ((p0.x + p1.x) / 2).toFixed(1)
    const x1 = p1.x.toFixed(1)
    const y1 = p1.y.toFixed(1)
    d += ` C${cp},${p0.y.toFixed(1)} ${cp},${y1} ${x1},${y1}`
  }
  return d
}

export const DevChart: FC<DevChartProps> = ({
  data,
  totalPoints,
  color,
  unit,
  formatValue = n => n.toFixed(1),
  fixedMax
}) => {
  const rawId = useId()
  const uid = rawId.replace(/[^a-zA-Z0-9]/g, '')
  const gradId = `dv-grad-${uid}`
  const clipId = `dv-clip-${uid}`

  const containerRef = useRef<HTMLDivElement>(null)
  const [dims, setDims] = useState({ w: 356, h: 180 })
  const hoverRef = useRef<{ idx: number; x: number; y: number } | null>(null)
  const [hover, setHover] = useState<{
    idx: number
    x: number
    y: number
  } | null>(null)

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect
      setDims({ w: Math.round(width), h: Math.round(height) })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const innerW = dims.w - PAD.left - PAD.right
  const innerH = dims.h - PAD.top - PAD.bottom

  const maxVal = useMemo(() => {
    if (fixedMax != null) return fixedMax
    const m = Math.max(...data, 1)
    return m * 1.15
  }, [data, fixedMax])

  const pts: Pt[] = useMemo(
    () =>
      data.map((v, i) => ({
        x: PAD.left + (i / (data.length - 1 || 1)) * innerW,
        y: PAD.top + innerH - (v / maxVal) * innerH
      })),
    [data, innerW, innerH, maxVal]
  )

  const linePath = useMemo(() => buildPath(pts), [pts])

  const areaPath = useMemo(() => {
    if (pts.length < 2) return ''
    const bottom = PAD.top + innerH
    return (
      linePath +
      ` L${pts[pts.length - 1].x.toFixed(1)},${bottom.toFixed(1)}` +
      ` L${pts[0].x.toFixed(1)},${bottom.toFixed(1)} Z`
    )
  }, [linePath, pts, innerH])

  const yTicks = useMemo(() => {
    const actualMax = Math.max(...data, 1)
    return Array.from({ length: Y_TICKS }, (_, i) => {
      const frac = i / (Y_TICKS - 1)
      const val = actualMax * frac
      const y = PAD.top + innerH - frac * innerH
      return { val, y }
    })
  }, [data, innerH])

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (pts.length < 2) return
      const rect = e.currentTarget.getBoundingClientRect()
      const mx = e.clientX - rect.left
      const relX = mx - PAD.left
      const frac = Math.max(0, Math.min(1, relX / innerW))
      const idx = Math.round(frac * (data.length - 1))
      const pt = pts[idx]
      if (!pt) return
      const next = { idx, x: pt.x, y: pt.y }
      hoverRef.current = next
      setHover(next)
    },
    [pts, innerW, data.length]
  )

  const handleMouseLeave = useCallback(() => {
    hoverRef.current = null
    setHover(null)
  }, [])

  const tooltipVal = hover !== null ? data[hover.idx] : null
  const secsAgo =
    hover !== null
      ? totalPoints - 1 - (totalPoints - data.length) - hover.idx
      : 0
  const tooltipLabel =
    hover !== null
      ? tooltipVal !== null
        ? secsAgo === 0
          ? 'now'
          : `${secsAgo}s ago`
        : ''
      : ''

  const tooltipX =
    hover !== null
      ? Math.max(PAD.left, Math.min(hover.x, dims.w - PAD.right))
      : 0

  return (
    <div ref={containerRef} className={styles.chartContainer}>
      <svg
        width={dims.w}
        height={dims.h}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <defs>
          <linearGradient id={gradId} x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0%' stopColor={color} stopOpacity={0.35} />
            <stop offset='100%' stopColor={color} stopOpacity={0.02} />
          </linearGradient>
          <clipPath id={clipId}>
            <rect x={PAD.left} y={PAD.top} width={innerW} height={innerH} />
          </clipPath>
        </defs>

        {yTicks.map(({ val, y }, i) => (
          <g key={i}>
            <line
              x1={PAD.left}
              y1={y}
              x2={PAD.left + innerW}
              y2={y}
              stroke='rgba(255,255,255,0.07)'
              strokeDasharray='3 5'
            />
            <text
              x={PAD.left - 6}
              y={y}
              textAnchor='end'
              dominantBaseline='middle'
              fill='rgba(255,255,255,0.35)'
              fontSize={9}
            >
              {Math.round(val)}
            </text>
          </g>
        ))}

        {areaPath && (
          <path
            d={areaPath}
            fill={`url(#${gradId})`}
            clipPath={`url(#${clipId})`}
          />
        )}

        {linePath && (
          <path
            d={linePath}
            fill='none'
            stroke={color}
            strokeWidth={1.5}
            clipPath={`url(#${clipId})`}
          />
        )}

        {hover && (
          <>
            <line
              x1={hover.x}
              y1={PAD.top}
              x2={hover.x}
              y2={PAD.top + innerH}
              stroke='rgba(255,255,255,0.2)'
              strokeWidth={1}
              strokeDasharray='3 3'
            />
            <circle
              cx={hover.x}
              cy={hover.y}
              r={4}
              fill={color}
              stroke='rgba(255,255,255,0.9)'
              strokeWidth={1.5}
            />
          </>
        )}
      </svg>

      {hover && tooltipVal !== null && (
        <div
          className={styles.tooltip}
          style={{ left: tooltipX, bottom: PAD.bottom + 6 }}
        >
          <span className={styles.tooltipVal}>
            {formatValue(tooltipVal)} {unit}
          </span>
          <span className={styles.tooltipTime}>{tooltipLabel}</span>
        </div>
      )}
    </div>
  )
}
